// Per-kind merged BufferGeometry cache for batched voxel rendering (P0.1-full).
//
// Each prefab's voxels are baked into a single BufferGeometry with per-vertex
// color + roughness + metalness + emissive (P3.3). The batched renderer then
// draws every entity sharing that prefab with one InstancedMesh, where each
// instance is the whole prefab (vs the old per-entity InstancedMesh where
// each instance was a single voxel).
//
// See docs/superpowers/plans/2026-06-23-voxel-batching-p0_1-full.md.
import type { BlockVoxel, HiFiTraits } from '@aod/ui/prefabs';
import {
  VOXEL_SCALE,
  getBuildingPrefab,
  getCreaturePrefab,
  materialClassOf,
} from '@aod/ui/prefabs';
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { smoothPartGeometry } from './smoothSurface.js';

// BoxGeometry produces 24 position vertices (4 per face × 6 faces) for
// per-face normals. Every per-vertex attribute needs to match that count.
const BOX_VERTS = 24;

// High-fidelity mode: chamfered, smooth-normaled voxels (RoundedBoxGeometry,
// 2 segments). Several× the vertex count of a sharp box — behind a toggle.
const HIFI_SEGMENTS = 2;
const HIFI_RADIUS = VOXEL_SCALE * 0.16;

// Defaults match MeshStandardMaterial's roughness=0.5, metalness=0.1.
// Voxels without explicit overrides use these.
const DEFAULT_ROUGHNESS = 0.5;
const DEFAULT_METALNESS = 0.1;

const creatureGeoCache = new Map<string, THREE.BufferGeometry>();
const buildingGeoCache = new Map<string, THREE.BufferGeometry>();

// Module-level temporaries — mutated in place, never reallocated.
const _color = new THREE.Color();
const _emissive = new THREE.Color();

/**
 * `teamMode`: 1/0 per voxel for prefabs that mark team voxels; -1 on every
 * vertex for prefabs that don't (the shader then applies the legacy light
 * whole-body faction tint instead).
 */
function boxForVoxel(
  v: BlockVoxel,
  legacyTint: boolean,
  hiFi = false,
  textured = false,
): THREE.BufferGeometry {
  let box: THREE.BufferGeometry;
  if (hiFi) {
    box = new RoundedBoxGeometry(VOXEL_SCALE, VOXEL_SCALE, VOXEL_SCALE, HIFI_SEGMENTS, HIFI_RADIUS);
  } else {
    box = new THREE.BoxGeometry(VOXEL_SCALE, VOXEL_SCALE, VOXEL_SCALE);
  }
  box.translate(v.x * VOXEL_SCALE, v.y * VOXEL_SCALE, v.z * VOXEL_SCALE);

  _color.set(v.color);
  _emissive.set(v.emissive ?? '#000000');
  const rough = v.roughness ?? DEFAULT_ROUGHNESS;
  const metal = v.metalness ?? DEFAULT_METALNESS;

  const vertCount = box.attributes.position?.count ?? BOX_VERTS;
  const colors = new Float32Array(vertCount * 3);
  const roughness = new Float32Array(vertCount);
  const metalness = new Float32Array(vertCount);
  const emissive = new Float32Array(vertCount * 3);
  const team = new Float32Array(vertCount).fill(legacyTint ? -1 : v.team ? 1 : 0);
  for (let i = 0; i < vertCount; i++) {
    colors[i * 3] = _color.r;
    colors[i * 3 + 1] = _color.g;
    colors[i * 3 + 2] = _color.b;
    roughness[i] = rough;
    metalness[i] = metal;
    emissive[i * 3] = _emissive.r;
    emissive[i * 3 + 1] = _emissive.g;
    emissive[i * 3 + 2] = _emissive.b;
  }
  box.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  box.setAttribute('aRoughness', new THREE.BufferAttribute(roughness, 1));
  box.setAttribute('aMetalness', new THREE.BufferAttribute(metalness, 1));
  box.setAttribute('aEmissive', new THREE.BufferAttribute(emissive, 3));
  box.setAttribute('aTeam', new THREE.BufferAttribute(team, 1));
  // Buildings share this material but are not textured: class 0 is "leave the colour alone".
  box.setAttribute(
    'aMat',
    new THREE.BufferAttribute(
      new Float32Array(vertCount).fill(textured ? materialClassOf(v) : 0),
      1,
    ),
  );
  return box;
}

/**
 * Fill a part geometry's per-vertex attributes from a representative voxel.
 * Same attribute set as boxForVoxel — the merged result must be uniform.
 */
function finishPartGeometry(
  input: THREE.BufferGeometry,
  v: BlockVoxel,
  legacyTint: boolean,
): THREE.BufferGeometry {
  // Primitives arrive indexed, RoundedBoxGeometry doesn't — mergeGeometries
  // needs one or the other.
  const geo = input.index ? input.toNonIndexed() : input;
  _color.set(v.color);
  _emissive.set(v.emissive ?? '#000000');
  const rough = v.roughness ?? DEFAULT_ROUGHNESS;
  const metal = v.metalness ?? DEFAULT_METALNESS;
  const vertCount = geo.attributes.position?.count ?? 0;
  const colors = new Float32Array(vertCount * 3);
  const roughness = new Float32Array(vertCount);
  const metalness = new Float32Array(vertCount);
  const emissive = new Float32Array(vertCount * 3);
  const team = new Float32Array(vertCount).fill(legacyTint ? -1 : v.team ? 1 : 0);
  for (let i = 0; i < vertCount; i++) {
    colors[i * 3] = _color.r;
    colors[i * 3 + 1] = _color.g;
    colors[i * 3 + 2] = _color.b;
    roughness[i] = rough;
    metalness[i] = metal;
    emissive[i * 3] = _emissive.r;
    emissive[i * 3 + 1] = _emissive.g;
    emissive[i * 3 + 2] = _emissive.b;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geo.setAttribute('aRoughness', new THREE.BufferAttribute(roughness, 1));
  geo.setAttribute('aMetalness', new THREE.BufferAttribute(metalness, 1));
  geo.setAttribute('aEmissive', new THREE.BufferAttribute(emissive, 3));
  geo.setAttribute('aTeam', new THREE.BufferAttribute(team, 1));
  geo.setAttribute(
    'aMat',
    new THREE.BufferAttribute(new Float32Array(vertCount).fill(materialClassOf(v)), 1),
  );
  return geo;
}

/** Most common voxel colour in a part (the primitive's body tint). */
function dominantColor(voxels: readonly BlockVoxel[]): BlockVoxel {
  const counts = new Map<string, number>();
  let best = voxels[0]!;
  let bestN = 0;
  for (const v of voxels) {
    const n = (counts.get(v.color) ?? 0) + 1;
    counts.set(v.color, n);
    if (n > bestN) {
      bestN = n;
      best = v;
    }
  }
  return best;
}

/**
 * Smooth sphere spanning a part's voxel bounds (hi-fi heads): organic crown
 * instead of a stack of cubes. Colour from the part's dominant voxel.
 */
function partSphere(
  voxels: readonly BlockVoxel[],
  legacyTint: boolean,
): THREE.BufferGeometry | null {
  if (voxels.length === 0) return null;
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  for (const v of voxels) {
    minX = Math.min(minX, v.x);
    minY = Math.min(minY, v.y);
    minZ = Math.min(minZ, v.z);
    maxX = Math.max(maxX, v.x + 1);
    maxY = Math.max(maxY, v.y + 1);
    maxZ = Math.max(maxZ, v.z + 1);
  }
  const wS = (maxX - minX) * VOXEL_SCALE;
  const hS = (maxY - minY) * VOXEL_SCALE;
  const dS = (maxZ - minZ) * VOXEL_SCALE;
  const r = Math.max(wS, hS, dS) / 2;
  if (r <= 0) return null;
  const geo = new THREE.SphereGeometry(r, 12, 10);
  geo.scale(wS / (2 * r) || 1, hS / (2 * r) || 1, dS / (2 * r) || 1);
  geo.translate(
    ((minX + maxX) / 2) * VOXEL_SCALE,
    ((minY + maxY) / 2) * VOXEL_SCALE,
    ((minZ + maxZ) / 2) * VOXEL_SCALE,
  );
  return finishPartGeometry(geo, dominantColor(voxels), legacyTint);
}

/**
 * Capsule along a limb part's longest extent (hi-fi arms/legs): one smooth
 * organic limb instead of a chain of cubes.
 */
function partCapsule(
  voxels: readonly BlockVoxel[],
  legacyTint: boolean,
): THREE.BufferGeometry | null {
  if (voxels.length === 0) return null;
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  for (const v of voxels) {
    minX = Math.min(minX, v.x);
    minY = Math.min(minY, v.y);
    minZ = Math.min(minZ, v.z);
    maxX = Math.max(maxX, v.x + 1);
    maxY = Math.max(maxY, v.y + 1);
    maxZ = Math.max(maxZ, v.z + 1);
  }
  const wS = (maxX - minX) * VOXEL_SCALE;
  const hS = (maxY - minY) * VOXEL_SCALE;
  const dS = (maxZ - minZ) * VOXEL_SCALE;
  // Limbs are vertical by convention; the radius comes from the thin axes.
  const radius = Math.max(Math.min(wS, dS) / 2, VOXEL_SCALE * 0.3);
  const length = Math.max(0, hS - radius * 2);
  const geo = new THREE.CapsuleGeometry(radius, length, 3, 10);
  geo.translate(
    ((minX + maxX) / 2) * VOXEL_SCALE,
    ((minY + maxY) / 2) * VOXEL_SCALE,
    ((minZ + maxZ) / 2) * VOXEL_SCALE,
  );
  return finishPartGeometry(geo, dominantColor(voxels), legacyTint);
}

/**
 * Vertical ambient shading over the merged unit: feet ~18% darker than the
 * crown — a cheap AO read that stops smooth parts from looking flat-lit.
 */
function applyVerticalShade(geo: THREE.BufferGeometry): void {
  geo.computeBoundingBox();
  const minY = geo.boundingBox?.min.y ?? 0;
  const spanY = Math.max(1e-6, (geo.boundingBox?.max.y ?? 1) - minY);
  const col = geo.getAttribute('color') as THREE.BufferAttribute;
  const pos = geo.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const t = (pos.getY(i) - minY) / spanY;
    const shade = 0.82 + 0.18 * t;
    col.setXYZ(i, col.getX(i) * shade, col.getY(i) * shade, col.getZ(i) * shade);
  }
  col.needsUpdate = true;
}

// --- Stage v3: proportions, wear, faction trim ---------------------------------------------------------------

/** Parts that follow the species build (width/depth); weapons, shields and capes keep their authored size. */
const BODY_PART_RE = /(torso|body|chest|robe|head|arm|leg|wing)/i;
const LIMB_PART_RE = /(arm|leg|wing)/i;

/**
 * Apply a species' proportions to one hi-fi part, in place: body parts scale in x/z around the unit's
 * vertical axis (`cx`, `cz`), limbs also thicken around their own axis. Heights are untouched, so a
 * champion is broader, not taller, and an archer lither, not shorter.
 */
export function applyProportions(
  geo: THREE.BufferGeometry,
  partName: string,
  traits: HiFiTraits | undefined,
  cx: number,
  cz: number,
): void {
  if (!traits || !BODY_PART_RE.test(partName)) return;
  const build = traits.build ?? 1;
  const limb = LIMB_PART_RE.test(partName) ? (traits.limb ?? 1) : 1;
  if (build === 1 && limb === 1) return;
  geo.computeBoundingBox();
  const b = geo.boundingBox;
  if (!b) return;
  const px = (b.min.x + b.max.x) / 2;
  const pz = (b.min.z + b.max.z) / 2;
  const pos = geo.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    // Limb thickness about the part's own axis, then the body build about the unit's axis.
    const x = px + (pos.getX(i) - px) * limb;
    const z = pz + (pos.getZ(i) - pz) * limb;
    pos.setX(i, cx + (x - cx) * build);
    pos.setZ(i, cz + (z - cz) * build);
  }
  pos.needsUpdate = true;
}

/** Deterministic [0,1) hash of a position and a seed (stable across builds and machines). */
function wearHash(x: number, y: number, z: number, seed: number): number {
  const s = Math.sin(x * 127.1 + y * 311.7 + z * 74.7 + seed * 19.19) * 43758.5453;
  return s - Math.floor(s);
}

/** Cheap string hash for a per-prefab wear seed. */
function seedOf(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 9973;
  return h;
}

/**
 * Build-time wear and detail normals: a small seeded tilt of each vertex normal (so smooth parts catch the
 * light unevenly, like hand-worn leather and metal) and a few percent of colour variation that darkens
 * toward the underside. Pure per-vertex attribute edits: no new attributes, no new vertices, so the merge
 * contract holds. Team-coloured and emissive vertices are left alone (a glowing rune must not mottle).
 */
export function applyWear(geo: THREE.BufferGeometry, seed: number): void {
  const pos = geo.getAttribute('position') as THREE.BufferAttribute;
  const nor = geo.getAttribute('normal') as THREE.BufferAttribute;
  const col = geo.getAttribute('color') as THREE.BufferAttribute;
  const team = geo.getAttribute('aTeam') as THREE.BufferAttribute | undefined;
  const emi = geo.getAttribute('aEmissive') as THREE.BufferAttribute | undefined;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const h1 = wearHash(x, y, z, seed);
    const h2 = wearHash(z, x, y, seed + 7);
    const h3 = wearHash(y, z, x, seed + 13);
    // Normal: tilt by up to ~0.1 rad, then renormalise.
    let nx = nor.getX(i) + (h1 - 0.5) * 0.2;
    let ny = nor.getY(i) + (h2 - 0.5) * 0.2;
    let nz = nor.getZ(i) + (h3 - 0.5) * 0.2;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len;
    ny /= len;
    nz /= len;
    nor.setXYZ(i, nx, ny, nz);
    const special =
      (team ? team.getX(i) > 0 : false) ||
      (emi ? emi.getX(i) + emi.getY(i) + emi.getZ(i) > 0 : false);
    if (special) continue;
    // Colour wear: +-4% mottling, a touch darker where the normal faces down (grime in the creases).
    const mottle = 1 + (h1 - 0.5) * 0.08 - Math.max(0, -ny) * 0.04;
    col.setXYZ(i, col.getX(i) * mottle, col.getY(i) * mottle, col.getZ(i) * mottle);
  }
  nor.needsUpdate = true;
  col.needsUpdate = true;
}

/** Faction trim: gold-toned metal that picks up a share of the owner's colour (aTeam 0.35 in the shader). */
const TRIM_VOXEL = (x: number, y: number, z: number): BlockVoxel => ({
  x,
  y,
  z,
  color: '#d9c27a',
  metalness: 0.85,
  roughness: 0.3,
  team: false,
});
const TRIM_TEAM_SHARE = 0.35;
const TRIM_THICK = VOXEL_SCALE * 0.2;
/** How far the frame stands off the face: tiny, so a unit's bounding size barely moves. */
const TRIM_LIFT = VOXEL_SCALE * 0.02;

/**
 * Trim for a part's team voxels (tabards, shield faces, capes): a thin raised frame around the team region on
 * its front and back faces. One frame per part, eight small bars, so the cost stays fixed however big the
 * tabard is. Returns null when the part has no team voxels. The frame carries a fractional `aTeam`, so the
 * shared geometry shows each owner's colour in its own trim: one geometry per prefab still holds.
 */
export function teamTrim(voxels: readonly BlockVoxel[]): THREE.BufferGeometry | null {
  const team = voxels.filter((v) => v.team);
  if (team.length === 0) return null;
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  for (const v of team) {
    minX = Math.min(minX, v.x);
    minY = Math.min(minY, v.y);
    minZ = Math.min(minZ, v.z);
    maxX = Math.max(maxX, v.x + 1);
    maxY = Math.max(maxY, v.y + 1);
    maxZ = Math.max(maxZ, v.z + 1);
  }
  const w = (maxX - minX) * VOXEL_SCALE;
  const h = (maxY - minY) * VOXEL_SCALE;
  const cx = ((minX + maxX) / 2) * VOXEL_SCALE;
  const cy = ((minY + maxY) / 2) * VOXEL_SCALE;
  if (w < VOXEL_SCALE * 1.5 || h < VOXEL_SCALE * 1.5) return null; // too small to frame
  const bars: THREE.BufferGeometry[] = [];
  const bar = (sx: number, sy: number, sz: number, x: number, y: number, z: number) => {
    const g = new RoundedBoxGeometry(sx, sy, sz, 1, Math.min(sx, sy, sz) * 0.3);
    g.translate(x, y, z);
    bars.push(finishPartGeometry(g, TRIM_VOXEL(0, 0, 0), false));
  };
  for (const zFace of [minZ * VOXEL_SCALE - TRIM_LIFT, maxZ * VOXEL_SCALE + TRIM_LIFT]) {
    bar(w, TRIM_THICK, TRIM_THICK, cx, minY * VOXEL_SCALE + TRIM_THICK / 2, zFace);
    bar(w, TRIM_THICK, TRIM_THICK, cx, maxY * VOXEL_SCALE - TRIM_THICK / 2, zFace);
    bar(TRIM_THICK, h, TRIM_THICK, minX * VOXEL_SCALE + TRIM_THICK / 2, cy, zFace);
    bar(TRIM_THICK, h, TRIM_THICK, maxX * VOXEL_SCALE - TRIM_THICK / 2, cy, zFace);
  }
  for (const g of bars) {
    (g.getAttribute('aTeam') as THREE.BufferAttribute).array.fill(TRIM_TEAM_SHARE);
  }
  return mergeGeometries(bars, false);
}

/** Primitive-substitution rules for the hi-fi path (part name → shape). */
function hiFiPartGeometry(
  name: string,
  voxels: readonly BlockVoxel[],
  legacyTint: boolean,
): THREE.BufferGeometry | null {
  // Parts carrying per-voxel semantics (team tabards, glowing runes) keep
  // the per-voxel rounded boxes — primitives can't express them.
  const hasSpecial = voxels.some((v) => v.team || v.emissive);
  // Every part becomes one smooth surface (heads and limbs too: a face, a boot or a claw is
  // colour on the surface, and a single-tint sphere or capsule would erase it); per-voxel
  // team/glow flags ride on the vertices. Parts too small to smooth fall through below.
  const smooth = smoothPartGeometry(voxels, legacyTint, {
    roughness: DEFAULT_ROUGHNESS,
    metalness: DEFAULT_METALNESS,
  });
  if (smooth) {
    const trim = hasSpecial && !legacyTint ? teamTrim(voxels) : null;
    return trim ? mergeGeometries([smooth, trim], false) : smooth;
  }
  if (hasSpecial) {
    const parts = voxels.map((v) => boxForVoxel(v, legacyTint, true, true));
    const trim = legacyTint ? null : teamTrim(voxels);
    if (trim) parts.push(trim);
    return parts.length > 0 ? mergeGeometries(parts, false) : null;
  }
  if (/head/i.test(name)) return partSphere(voxels, legacyTint);
  if (/(arm|leg|wing)/i.test(name)) return partCapsule(voxels, legacyTint);
  return null; // torso/detail: fall through to per-voxel rounded boxes
}

function buildCreatureGeometry(prefabId: string, hiFi = false): THREE.BufferGeometry | null {
  const prefab = getCreaturePrefab(prefabId);
  if (!prefab) return null;

  const legacyTint = !prefab.parts.some((part) => part.voxels.some((v) => v.team));

  if (hiFi) {
    // Primitive-substitution path: heads → spheres, limbs → capsules,
    // everything else (torso/detail/per-voxel-semantics) → rounded boxes.
    // Detail-overdraw (the later-wins dedupe below) doesn't apply — each
    // part renders once, whole.
    // The unit's vertical axis (for the species build): the centre of every body voxel.
    let sumX = 0;
    let sumZ = 0;
    let n = 0;
    for (const part of prefab.parts) {
      if (!BODY_PART_RE.test(part.name)) continue;
      for (const v of part.voxels) {
        sumX += (v.x + 0.5) * VOXEL_SCALE;
        sumZ += (v.z + 0.5) * VOXEL_SCALE;
        n++;
      }
    }
    const cx = n > 0 ? sumX / n : 0;
    const cz = n > 0 ? sumZ / n : 0;
    const parts: THREE.BufferGeometry[] = [];
    for (const part of prefab.parts) {
      if (part.voxels.length === 0) continue;
      const prim = hiFiPartGeometry(part.name, part.voxels, legacyTint);
      let geo: THREE.BufferGeometry;
      if (prim) {
        geo = prim;
      } else {
        const boxes = part.voxels.map((v) => boxForVoxel(v, legacyTint, true, true));
        geo = boxes.length === 1 ? boxes[0]! : mergeGeometries(boxes, false);
      }
      applyProportions(geo, part.name, prefab.hifi, cx, cz);
      parts.push(geo);
    }
    if (parts.length === 0) return null;
    const merged = mergeGeometries(parts, false);
    applyWear(merged, seedOf(prefabId));
    applyVerticalShade(merged);
    return merged;
  }

  // Later voxels win at a shared position: prefabs draw detail (trim,
  // helmets over heads, sails over masts, aprons over torsos) after the base
  // shape. First-wins silently dropped that detail on 31 of 44 prefabs.
  const byPosition = new Map<string, BlockVoxel>();
  for (const part of prefab.parts) {
    for (const v of part.voxels) byPosition.set(`${v.x},${v.y},${v.z}`, v);
  }
  const parts = [...byPosition.values()].map((v) => boxForVoxel(v, legacyTint, false, true));
  if (parts.length === 0) return null;
  return mergeGeometries(parts, false);
}

function buildBuildingGeometry(prefabId: string): THREE.BufferGeometry | null {
  const prefab = getBuildingPrefab(prefabId);
  if (!prefab) return null;

  const legacyTint = !prefab.blocks.some((b) => b.team);
  // Later blocks win at a shared position (same rule as creatures).
  const byPosition = new Map<string, BlockVoxel>();
  for (const b of prefab.blocks) byPosition.set(`${b.x},${b.y},${b.z}`, b);
  const parts = [...byPosition.values()].map((b) => boxForVoxel(b, legacyTint));
  if (parts.length === 0) return null;
  return mergeGeometries(parts, false);
}

/**
 * Cached creature (unit) geometry lookup. Returns null for unknown/empty
 * prefabs. `hiFi` selects the rounded, smooth-normaled variant (separate
 * cache slot — the toggle swaps prefabs without rebuilding the lo-fi set).
 */
export function getCreatureGeometry(prefabId: string, hiFi = false): THREE.BufferGeometry | null {
  const key = `${hiFi ? 'hi' : 'lo'}:${prefabId}`;
  const cached = creatureGeoCache.get(key);
  if (cached) return cached;
  const built = buildCreatureGeometry(prefabId, hiFi);
  if (built) creatureGeoCache.set(key, built);
  return built;
}

/** Cached structure (building) geometry lookup. Returns null for unknown/empty prefabs. */
export function getBuildingGeometry(prefabId: string): THREE.BufferGeometry | null {
  const cached = buildingGeoCache.get(prefabId);
  if (cached) return cached;
  const built = buildBuildingGeometry(prefabId);
  if (built) buildingGeoCache.set(prefabId, built);
  return built;
}
